import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Plus, Trash2, ArrowDownLeft, ArrowUpRight, ArrowUpDown, Flame } from "lucide-react";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

const KIND = {
  purchase:   { label: "Purchase",   icon: ArrowDownLeft, colour: "hsl(132 18% 36%)" },
  adjustment: { label: "Adjustment", icon: ArrowUpDown,   colour: "hsl(31 65% 53%)" },
  roast:      { label: "Roast",      icon: Flame,         colour: "hsl(20 39% 39%)" },
};

export default function OriginTransactionsModal({ originId, canWrite, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({
    adjustment_date: new Date().toISOString().slice(0, 10),
    delta_kg: "", reason: "Adjustment", notes: "",
  });

  const load = async () => {
    if (!originId) return;
    try {
      const { data } = await api.get(`/origins/${originId}/transactions?order=desc`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [originId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/inventory-adjustments", {
        origin_id: originId,
        adjustment_date: adjForm.adjustment_date,
        delta_kg: parseFloat(adjForm.delta_kg) || 0,
        reason: adjForm.reason,
        notes: adjForm.notes,
      });
      setAdjOpen(false);
      setAdjForm({ ...adjForm, delta_kg: "", notes: "" });
      await load();
      onChanged?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const removeAdj = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    await api.delete(`/inventory-adjustments/${id}`);
    await load();
    onChanged?.();
  };

  if (!originId) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4" data-testid="origin-transactions-modal">
      <div className="bg-card border border-border rounded shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <p className="label-tiny">Inventory ledger</p>
            <h3 className="font-display text-xl font-bold">
              {data?.origin?.name || "Loading…"}
              {data && (
                <span className="ml-3 text-muted-foreground font-normal text-base">
                  Balance: <span className={`font-mono ${data.current_balance_kg < 0 ? "text-destructive" : ""}`}>
                    {fmt(data.current_balance_kg, 1)} kg
                  </span>
                </span>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Newest first · running balance computed chronologically</p>
          </div>
          <div className="flex items-center gap-2">
            {canWrite && (
              <button onClick={() => setAdjOpen(true)} data-testid="add-adjustment"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
                <Plus className="w-4 h-4" /> Adjustment
              </button>
            )}
            <button onClick={onClose} data-testid="origin-transactions-close" className="p-2 hover:bg-secondary rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {error && <p className="px-6 pt-3 text-destructive text-sm">{error}</p>}

        {data && (
          <div className="p-6">
            {data.transactions.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                No transactions yet for this origin. Add a purchase lot or adjustment.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="bg-secondary">
                    <th className="label-tiny px-3 py-2 text-left">Date</th>
                    <th className="label-tiny px-3 py-2 text-left">Type</th>
                    <th className="label-tiny px-3 py-2 text-left">Reference</th>
                    <th className="label-tiny px-3 py-2 text-right">Δ kg</th>
                    <th className="label-tiny px-3 py-2 text-right">Running balance kg</th>
                    <th className="label-tiny px-3 py-2 text-right">Value</th>
                    {canWrite && <th className="w-12" />}
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t, idx) => {
                    const k = KIND[t.kind] || { label: t.kind, icon: ArrowUpRight, colour: "hsl(25 8% 42%)" };
                    return (
                      <tr key={`${t.kind}-${t.id}-${idx}`} className="border-t border-border hover:bg-secondary/40">
                        <td className="px-3 py-2 font-mono text-xs">{t.date || "—"}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1.5" style={{ color: k.colour }}>
                            <k.icon className="w-3.5 h-3.5" />
                            {k.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{t.ref || "—"}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${t.delta_kg >= 0 ? "text-[hsl(132,18%,36%)]" : "text-destructive"}`}>
                          {t.delta_kg >= 0 ? "+" : ""}{fmt(t.delta_kg, 2)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${t.balance_kg < 0 ? "text-destructive" : ""}`}
                            data-testid={`txn-balance-${idx}`}>
                          {fmt(t.balance_kg, 2)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {t.kind === "purchase" && t.value ? `R ${fmt(t.value, 0)}` : "—"}
                        </td>
                        {canWrite && (
                          <td className="px-2 py-2 text-right">
                            {t.kind === "adjustment" && (
                              <button onClick={() => removeAdj(t.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {adjOpen && (
          <div className="border-t border-border bg-secondary/30 p-6">
            <h4 className="font-display font-bold mb-3">New inventory adjustment</h4>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input type="date" required value={adjForm.adjustment_date} onChange={(e) => setAdjForm({ ...adjForm, adjustment_date: e.target.value })}
                data-testid="adj-date"
                className="px-3 py-2 bg-white border border-border rounded text-sm" />
              <input type="number" step="any" required placeholder="Δ kg (+ or −)" value={adjForm.delta_kg}
                onChange={(e) => setAdjForm({ ...adjForm, delta_kg: e.target.value })}
                data-testid="adj-delta"
                className="px-3 py-2 bg-white border border-border rounded text-sm font-mono" />
              <select value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })}
                data-testid="adj-reason"
                className="px-3 py-2 bg-white border border-border rounded text-sm">
                <option>Adjustment</option>
                <option>Stock-take correction</option>
                <option>Write-off / damaged</option>
                <option>Sample / R&amp;D</option>
                <option>Transfer in</option>
                <option>Transfer out</option>
                <option>Found stock</option>
                <option>Opening balance</option>
              </select>
              <div className="flex gap-2">
                <button type="submit" data-testid="adj-submit"
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">Save</button>
                <button type="button" onClick={() => setAdjOpen(false)}
                  className="px-3 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
              </div>
              <input className="md:col-span-4 px-3 py-2 bg-white border border-border rounded text-sm"
                placeholder="Notes (optional)" value={adjForm.notes}
                onChange={(e) => setAdjForm({ ...adjForm, notes: e.target.value })} />
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
